

import React, {useState, useEffect} from "react";
import { filters } from "../../utils/filters";
import DefaultColumnFilter from "../Filters/DefaultColumnFilter";
import {
  useBlockLayout,
  useColumnOrder,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import useOptionFilter from "../../hooks/useOptionFilter";
import {
  IconButton,
  Pagination,
  Stack,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Chip,
} from "@mui/material";
import MaUTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { addTooltipRow } from "../../utils/addTooltipRow";
import NestedTable from "./NestedTable";
import styled from "@emotion/styled";
import ButtonGroupCustom from "../buttons/ButtonGroupCustom";
import { ArrowDownward, ArrowUpward, Circle } from "@mui/icons-material";
import BootstrapTooltip from "../tooltips/BootstrapTooltip";
import { toggleRefresh } from "../../../../../reducers/slices/tables/table.slice";
import { useDispatch } from "react-redux";
import LinkIcon from "@mui/icons-material/Link"; // Correct import for Link icon in MUI v5

import TagDialog from "../../../../../scenes/dashboard/components/genomics-dashboard/components/results/components/toolbar/tag_dialog/TagDialog";
import './TableComponent.css';

// TODO:put all these together in a general function in the same file
import { getTreatmentAnnotationIcon } from "../../../../../scenes/instand/treatments/utils";
import { getPharmacogenomicsAnnotationIcon } from "../../../../../scenes/instand/pharmacogenomics_table/utils";
import { getFusionAnnotationIcon } from "../../../../../scenes/instand/fusions_table/utils";
import { isUrl } from "../../utils/isUrl";
import GPAP_Dialog from "../../../../../gpap-lib/components/GPAP_Dialog";
import { dialogConfig } from "./dialog_delete_config";
import {Box} from "@mui/material";
import { renderToStringAlt } from "../../../../../scenes/instand/reports/create-report/shared-components/renderToString.js";
import { convertDate } from "../../../../../scenes/instand/reports/create-report/sections/utils.js";
import { useTranslation } from "react-i18next";

function TableComponent({
  columns,
  data,
  setData,
  type,
  nestedColumns,
  buttons,
  states,
  tooltipCompact,
  renderFeatures,
  chipFull,
  chipSmall,
  dataType,
  pagination=true,
  enableTag,
  analysisType
}) {
  const { t } = useTranslation(); 
  const { customFeatures } = states;
  const { showHeader } = customFeatures.find((obj) => "showHeader" in obj);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [deleteFunction, setDeleteFunction] = useState(null);

  const [expandedRows, setExpandedRows] = useState({});

  const filterTypes = React.useMemo(filters, []);

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );
  const [tableData, setTableData] = useState(data);
  const [idField, setIdField] = useState(null);

  // define if the table is taggable;
  const isTaggable =  enableTag


  useEffect(() => {
    setTableData(data);
  }, [data]);

  const handleOpenDeleteDialog = (idRelation, deleteFunction, idField) => {
    setSelectedRowId(idRelation);
    setDeleteFunction(() => deleteFunction); 
    setIdField(idField);
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRowId(null);
  };
  const dispatch = useDispatch();
  
  const handleDelete = () => {
    if (selectedRowId) {
      deleteFunction(selectedRowId)
        .then((response) => {
          if (response.ok) {
            const updatedData = tableData.filter(
              (item) => item[idField] !== selectedRowId
            );
            setTableData(updatedData); 
            setData(updatedData); 
            dispatch(toggleRefresh());
            return response.json();
          } else {
            throw new Error(response.statusText);
          }
        })
        .catch((error) => {
          console.error("ERROR", error);
          alert(renderToStringAlt("An error occurred")); 
        });
    }
    handleCloseDialog();
  };

  const { title, text } = dialogConfig[states.reportType] || dialogConfig.dir;

  const sortNum = React.useCallback((rowA, rowB, columnId) => {
    const valA = parseFloat(rowA.values[columnId]) || 0;
    const valB = parseFloat(rowB.values[columnId]) || 0;
    if (valA > valB) {
      return 1;
    } else if (valA < valB) {
      return -1;
    } else {
      return 0;
    }
  }, []);

  const sortDate = React.useCallback((rowA, rowB, columnId) => {
    return new Date(rowA.values[columnId]) - new Date(rowB.values[columnId]);
  }, []);
  
 const numColumns = React.useMemo(() => {
  return columns.map((group) => {
    const newColumns = group.columns.map((column) => {
      if (column.type === "date") {
        return {
          ...column,
          sortType: sortDate,
        };
      }
    if (column.type === "number") {
          return {
            ...column,
            sortType: sortNum,
          };
        }
        return column;
      });

      return {
        ...group,
        columns: newColumns,
      };
    });
  }, [columns, sortNum]);


  const getAnnotationIcon = (row, participant) => {
    if (states.classRow === 'Treatments') {
      return getTreatmentAnnotationIcon(row, participant, analysisType);
    } else if (states.classRow === 'Pharmacogenomics') {
      return getPharmacogenomicsAnnotationIcon(row, participant);
    } 
    else if(states.classRow === 'Fusions') {  
      return getFusionAnnotationIcon(row, participant);
    }
    else {
      return null;
    }
  };

const isTagged = (rowData) => {
  if (!rowData) return false;
  const fakeRow = { values: rowData };
  return getAnnotationIcon(fakeRow, states.participant) !== null;
};

const dataWithIsTagged = React.useMemo(() => {
  return data.map((row) => ({
    ...row,
    isTagged: isTagged(row),
  }));
}, [data, states.participant, states.classRow]);

 
const columnsWithTag = React.useMemo(() => {
  if (!isTaggable) {
    return numColumns; 
  }
  const tagColumn = {
    Header: 'Tag',
    accessor: 'isTagged',
    disableFilters: true,
    Cell: ({ row }) => {
      const fakeRow = { values: row.original };
      const icon = getAnnotationIcon(fakeRow, states.participant);
      return icon;
    },
    sortType: (rowA, rowB) => {
      const a = rowA.values.isTagged ? 1 : 0;
      const b = rowB.values.isTagged ? 1 : 0;
      return b - a;
    },
  };
  return [
    tagColumn,
    ...columns,
  ];
}, [columns, states.participant, states.classRow, isTaggable]);



  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    visibleColumns,
    page,
    pageCount,
    gotoPage,
    setPageSize,
    allColumns,
    getToggleHideAllColumnsProps,
    state: { pageIndex, pageSize },
    resetResizing,
    rows, 
  } = useTable(
    {
      columns: columnsWithTag,
    data: dataWithIsTagged,
      defaultColumn,
      filterTypes,
      initialState: { pageIndex: 0 },
      autoResetSortBy: false
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useBlockLayout,
    useResizeColumns,
    useOptionFilter(
      columns,
      type,
      states,
      setExpandedRows,
      tableData,
      handleOpenDeleteDialog)
    );

    useEffect(() => {
      if (pageIndex === pageCount - 1) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, [pageIndex, pageCount]);
    
    
  
  const firstPageRows = page;
  const hasData = data.length > 0;
  const hasPagination = pagination && hasData;


  const [selectedRow, setSelectedRow] = useState(null);

  const renderSortIcon = (column) => {
    if (column.isSorted) {
      if (column.isSortedDesc) {
        return (
            <IconButton>
              <ArrowUpward />
            </IconButton>
        );
      } else {
        return (
            <IconButton>
              <ArrowDownward />
            </IconButton>
        );
      }
    }
    return null; // Return nothing if the column is not sorted
  };


  return (
    <>
    <Stack direction="row" spacing={2} style={{paddingBottom: "8px"}}>
    {enableTag  &&
    <Box p={1}>
     <TagDialog
          annotate_variants = {(label, array, type) => console.log(type)}  // edit. See how to add tagged variants to the store
          all_tagged_variants={ states.all_tagged_variants}
          getAllTaggedVariants = {states.getAllTaggedVariants}
          dataType={dataType}
      />
       </Box>
    }

      <ButtonGroupCustom
        allColumns={allColumns}
        getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
        resetResizing={resetResizing}
        buttons={buttons}
        states={states}
        renderFeatures={renderFeatures}
      />
        </Stack>

      <TableContainer>
      <MaUTable {...getTableProps()}
      sx={{ width: "auto", tableLayout: "fixed" }} >
     
     <TableHead>
  {headerGroups.map((headerGroup, headerGroupIndex) => (
    <React.Fragment key={headerGroup.id}>
      <TableRow 
        sx={{
          backgroundColor: headerGroupIndex === 0 ? '#3E69A9' : (showHeader ? '#EAEAEA' : '#3E69A9'),
          color: headerGroupIndex === 0 ? 'white' : (showHeader ? 'black' : 'white'), 
          display: headerGroupIndex === 0 ? (showHeader ? 'table-row' : 'none !important') : 'table-row',
          '& th, & td': { 
            padding: '10px',
            lineHeight: 'normal', 
            alignItems: 'center', 
            borderRight: '1px solid white', 
            position: 'sticky',
            top: 0,
            zIndex: 1,
            '&:last-child': {
              borderRight: 0
            },
            '&:first-child': {
              width: customFeatures.find(feature => feature.toggle || feature.expanding) ? '60px!important' : 'auto', 
              minWidth: customFeatures.find(feature => feature.toggle || feature.expanding) ? '60px!important' : 'auto',
              maxWidth: customFeatures.find(feature => feature.toggle || feature.expanding) ? '60px!important' : 'auto',
            }
          }
        }} 
        {...headerGroup.getHeaderGroupProps()}
      >
        {headerGroup.headers.map((column, columnIndex) => (
          <TableCell 
            {...column.getHeaderProps(column.getSortByToggleProps())}
            sx={{
              width: columnIndex === 0 && customFeatures.find(feature => feature.toggle || feature.expanding) ? '20px' : 'auto'  
            }}
          >
            <div style={{ color: headerGroupIndex === 0 ? 'white' : (showHeader ? 'inherit' : 'white') }}> 
              {column.tooltip ? (
                <BootstrapTooltip title={column.tooltip} forceUnderline={true}>
                  {column.render("Header")}
                </BootstrapTooltip>
              ) : (
                column.render("Header")
              )}
              <span>
               {renderSortIcon(column)}
              </span>
            </div>
          </TableCell>
        ))}
      </TableRow>

      {headerGroup.headers.some(column => column.canFilter) && (
 <TableRow  {...headerGroup.getHeaderGroupProps()} >
 {headerGroup.headers.map((column) => (
   <TableCell {...column.getHeaderProps()} 
   sx={{ 
     verticalAlign: 'middle',  
    
     '&:first-child': {
       borderRight: '0',
       ...(states.customFeatures.find(feature => feature.toggle || feature.expanding) && {
         width: '60px!important',
       }),
     },
     position: 'sticky', top: 0, zIndex: 1,
    }}    >
      {column.canFilter && column.filterType && column.filterType.type !== 'none' ? column.render('Filter') : null}
   </TableCell>
 ))}
</TableRow>
)}

    </React.Fragment>
  ))}
</TableHead>

          <TableBodyCustom
            rowId={states.idExpanded.id}
            rowName={states.classRow}
            {...getTableBodyProps()}
          >
            {firstPageRows.map((row, i) => {
             
              prepareRow(row);
              return (
                <>
                  <TableRowCustom
                    className={`${states.classRow}${row.id} ${selectedRow === row.id ? 'selected' : ''}`}
                    key={row.id}
                    {...row.getRowProps()}
                    rowId={states.idExpanded.id}
                    states={states}
                    expanded={states}
                    
                    onClick={() => {

                      setSelectedRow(row.id);
                      
                      if (states.setSelected !== undefined)
                        states.setSelected(row.original);

                    }}
                    onMouseEnter={() => {
           

                      if (row.id !== selectedRow) {
                        document.querySelector(`.${states.classRow}${row.id}`).classList.add('hovered');
                      }

                      document.querySelector(
                        `.${states.classRow}${row.id}`
                      ).style.cursor = "pointer";
                    }}
                    onMouseLeave={() => {
                      if (row.id !== selectedRow) {
                        document.querySelector(`.${states.classRow}${row.id}`).classList.remove('hovered');
                      }
                    
                    }}
                
                    >

               
    
                
                    {row.cells.map((cell) => {

                      const dateColumns = ["creation_date", "deadline"];


                      if (dateColumns.includes(cell.column.id) && cell.value) {
                        const dateObj = new Date(cell.value);
                        const formattedDate = dateObj.toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                        return (
                          <TableCell {...cell.getCellProps()}>
                            {formattedDate}
                          </TableCell>
                        );
                      }
                      if (cell.column.id === 'analysis' || cell.column.id === "origin") {
                        let modifiedValue = cell.value;
                        if (cell.value && typeof cell.value === 'string') {
                          const words = cell.value.replace(/_/g, ' ').split(' ');
                          words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
                          modifiedValue = words.join(' ');
                        }
                        return (
                          <TableCell {...cell.getCellProps()} style={{ width: '150px' }}>
                            {typeof modifiedValue === 'string' ? renderToStringAlt(modifiedValue) : modifiedValue}
                          </TableCell>
                        );
                      }
               
                      const { truncated, content } = addTooltipRow(
                        cell,
                        tooltipCompact,
                        tableData
                      );
                      const translatedContent =
                      typeof content === "string" ? renderToStringAlt(content) : content;
                      
                      return (
                        <TableCell {...cell.getCellProps()}>
                          <BootstrapTooltip 
                            title={truncated ? `${cell.value}` : ""} 
                            isTruncated={truncated}
                          >
                          <div style={truncated ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' } : {}}>
                          {(() => {
                             const isValueArray = Array.isArray(cell?.value);
                            const matchedDataIcons = chipSmall
                              ?.flatMap((item) => item.cells)
                              .find((data) => isValueArray ? cell?.value.includes(data.data) : data.data === cell?.value);
                            const matchedDataCells = chipFull
                              ?.flatMap((item) => item.cells)
                              .find((data) => isValueArray ? cell?.value.includes(data.data) : data.data === cell?.value);
                         

                            const formattedValue = typeof cell.value === 'string'
                              ? cell.value.charAt(0).toUpperCase() + cell.value.slice(1).toLowerCase()
                              : cell.value;
                            const translatedValue =
                                typeof formattedValue === 'string'
                                   ? renderToStringAlt(formattedValue)
                                   : formattedValue;

                            if (matchedDataIcons) {
                              return (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <Circle style={{ color: matchedDataIcons.color, fontSize: "12px" }} />
                                  <span style={{ marginLeft: '5px', color: matchedDataIcons.textColor }}>
                                    {translatedValue}
                                  </span>
                                </div>
                              );
                            } else if (matchedDataCells) {
                              return (
                                <Chip
                                  label={translatedValue}
                                  sx={{
                                    height: "20px",
                                    pl: "0px",
                                    pr: "0px",
                                    backgroundColor: matchedDataCells.color,
                                    color: matchedDataCells.textColor,
                                  }}
                                />
                              );
                            }
                        

                         
                                if (typeof cell.column.Header === "function") {
                                  return translatedContent;
                                }


                             

                                if (cell.column.id !== "VariantData") {
                                  let value;
                                  if (typeof cell.value === 'string') {
                                   
                                    let valuesArray = cell.value.split(' ').filter(val => val.trim().toLowerCase() !== "none");
                                    value = valuesArray.length > 0 ? valuesArray[0] : "None";
                                  } else {
                                    value = cell.value;
                                  }
                                

                                  if (isUrl(value) && value.trim().toLowerCase() !== "none") {
                                    return (
                                      <a
                                        style={{ textDecoration: "none" }}
                                        href={value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {cell.column.icon ? (
                                          <img src={cell.column.icon} alt="Link Icon" style={{ width: '20px', height: '20px' }} />
                                        ) : (
                                          <LinkIcon />
                                        )}
                                      </a>
                                    );
                                  }
                                }
                                

                                if (cell.column.id === 'links') {
                                 
                                  if (Array.isArray(cell.value) && cell.value.length > 0) {
                                    if (cell.value[0].link === "No Data") {
                                   
                                      return (
                                        <TableCell {...cell.getCellProps()}>
                                          No data
                                        </TableCell>
                                      );
                                    } else {
                                 
                                      return (
                                        <TableCell {...cell.getCellProps()}>
                                        {cell.value.map((linkObj, index) => (
                                        <Tooltip key={index} title={linkObj.link}>
                                          <a href={linkObj.link} target="_blank" rel="noopener noreferrer">
                                            <img src={linkObj.icon} alt="Link Icon" style={{ width: '20px', height: '20px' }} />
                                          </a>
                                        </Tooltip>
                                      ))}

                                      </TableCell>
                                      );
                                    }
                                  } else {
                               
                                    return (
                                      <TableCell {...cell.getCellProps()}>N/A</TableCell>
                                    );
                                  }
                                }
  
                                return (
                                  <div>
                                    {cell.value ? (
                                      translatedContent
                                    ) : cell.column.id === 'isTagged' ? (
                                      null
                                    ) : (
                                      <p className="score_NA">N/A</p>
                                    )}
                                  </div>
                                );
                             })()}
                            </div>
                          </BootstrapTooltip>
                        </TableCell>
                      );
                    })}
                  </TableRowCustom>

                  {row.isExpanded && expandedRows[row.id]?.expanded ? (
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#F9F9F9" }} colSpan={visibleColumns.length}>
                        {expandedRows[row.id]?.data ? ( 
                          <NestedTable
                            columns={nestedColumns}
                            data={expandedRows[row.id].data} 
                            states={states}
                          />
                        ) : (

                          <div>Loading...</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </>
              );
            })}
          </TableBodyCustom>
        </MaUTable>
      </TableContainer>
      <GPAP_Dialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        title={title}
        text= {text}
        onExitText="Cancel"
        onConfirmText="Delete"
        onExitAction={handleCloseDialog}
        onConfirmAction={handleDelete}
        type="delete"
      />

      <br />
    {hasPagination && (
      <div style={{ display: "flex", alignItems: "center", height: "50px", justifyContent: "center"}}>

      <TablePagination
      labelRowsPerPage={t("Rows per page:")}
      component="div"
      count={rows.length}
      rowsPerPage={pageSize}
      page={pageIndex}
      onPageChange={(event, newPage) => gotoPage(newPage)}
      onRowsPerPageChange={(event) => setPageSize(Number(event.target.value))}
      style={{ '& .MuiTablePagination-actions': { display: 'none' } }}
      labelDisplayedRows={({ from, to, count }) =>
          t("table_pagination_range", {
            page: pageIndex + 1,
            from,
            to,
            count
          })
        }
      />

      <Pagination
      style={{ marginLeft: '40px' }}
      count={pageCount}
      page={pageIndex + 1}
      onChange={(event, value) => gotoPage(value - 1)}
      showFirstButton
      showLastButton
      />


</div>
)}
</>
);
}


export default TableComponent;

const TableRowCustom = styled(TableRow)`
height: 40px!important;
& > td:first-child {
  ${(props) => props.states.customFeatures.find(feature => feature.toggle || feature.expanding) ? 'width: 60px!important;' : ''}
}

  & > td {
    display: flex!important;
    align-items: center;
    div {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;


const TableBodyCustom = styled(TableBody)`
  ${(props) => {
    return (
      props.rowId &&
      `.${props.rowName}${props.rowId} > td {
    background-color: #C6DBEF !important;
  }`
    );
  }}
`;